import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Plus, FileText, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, ArrowUpDown, Filter, RefreshCw, Box, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import StatusBadge from './StatusBadge';

const InvoiceList = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { canCreate } = usePermissions('invoices');
    const [showAllSites, setShowAllSites] = useState(false);
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';

    const searchParams = new URLSearchParams(location.search);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [limit] = useState(50);
    const [sort, setSort] = useState({
        column: searchParams.get('sortBy') || 'invoicedate',
        direction: searchParams.get('sortDir') || 'desc'
    });

    const [filters, setFilters] = useState({
        invoicenum: searchParams.get('invoicenum') || "",
        description: searchParams.get('description') || "",
        status: searchParams.get('status') || "",
        vendor: searchParams.get('vendor') || "",
        ponum: searchParams.get('ponum') || "",
        siteid: searchParams.get('siteid') || ""
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/invoices', {
                params: {
                    page,
                    limit,
                    all_sites: Boolean(showAllSites),
                    user_siteid: sessionSite,
                    orgid: sessionOrg,
                    filter_siteid: filters.siteid || "",
                    sortBy: sort.column,
                    sortDir: sort.direction,
                    invoicenum: filters.invoicenum,
                    description: filters.description,
                    status: filters.status,
                    vendor: filters.vendor,
                    ponum: filters.ponum,
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setInvoices(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            toast.error(t('invoices.messages.loadFailed', 'Failed to load Purchase Invoices'));
        } finally {
            setLoading(false);
        }
    }, [page, showAllSites, filters, sort, sessionSite, sessionOrg, limit, t]);

    useEffect(() => {
        const timer = setTimeout(() => { fetchData(); }, 400);
        return () => clearTimeout(timer);
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const handleRowClick = (invoicenum) => {
        const queryParams = new URLSearchParams({
            page,
            ...filters,
            sortBy: sort.column,
            sortDir: sort.direction,
            all_sites: showAllSites,
            orgid: sessionOrg
        }).toString();
        navigate(`/invoices/${invoicenum}?${queryParams}`);
    };

    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const clearFilters = () => {
        setFilters({ invoicenum: "", description: "", status: "", vendor: "", ponum: "", siteid: "" });
        setPage(1);
    };

    useEffect(() => {
        const openAllSites = () => { setShowAllSites(true); setPage(1); };
        const viewMySite = () => { setShowAllSites(false); setPage(1); };

        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div
            className={`flex flex-col h-full bg-surface-app text-[11px] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`}
            style={{ fontSize: `${fontSize}px` }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >

            {/* Toolbar */}
            <div className="bg-surface-card p-3 border-b shadow-sm z-20">
                <div className={`flex justify-between items-center px-4 `}>
                    <h2 className={`text-primary-dark font-black italic uppercase text-lg flex items-center gap-2 `}>
                        <FileText size={22} className="text-primary" />
                        {t('invoices.headers.invoiceList', 'Purchase Invoices')}
                        <RefreshCw
                            size={18}
                            className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary hover:text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`}
                            onClick={fetchData}
                        />
                    </h2>

                    <div className={`flex gap-3 `}>
                        <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
                            <X size={16}/> {t('po.actions.clearFilters', 'CLEAR FILTERS')}
                        </button>
                        {canCreate && (
                            <button onClick={() => navigate('/invoices/new')} className={`bg-primary-dark text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-primary shadow-lg active:scale-95 transition-all `}>
                                <Plus size={16} /> {t('invoices.actions.newInvoice', 'NEW INVOICE')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
                            <tr>
                                <SortableHeader label={t('invoices.tables.invoiceNum', 'Invoice Num')} sortKey="invoicenum" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('po.fields.vendor', 'Vendor')} sortKey="vendor" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('invoices.fields.ponum', 'Reference PO')} sortKey="ponum" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <th className="p-3 border-x border-white/10 text-center">{t('assets.headers.status', 'Status')}</th>
                                <SortableHeader label={t('invoices.tables.grandTotal', 'Grand Total')} sortKey="grand_total" currentSort={sort} onSort={handleSort} align="right" isRTL={isRTL} />
                                <SortableHeader label={t('invoices.tables.invoiceDate', 'Invoice Date')} sortKey="invoicedate" currentSort={sort} onSort={handleSort} align="center" isRTL={isRTL} />
                                <th className="p-3 text-center border-x border-white/10">{t('assets.headers.site', 'Site')}</th>
                            </tr>
                            <tr className={`bg-surface-panel shadow-inner `}>
                                <td className="p-1 border-x border-line">
                                    <input className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter', 'Filter')} value={filters.invoicenum} onChange={e => handleFilterChange('invoicenum', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-line">
                                    <input className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter', 'Filter')} value={filters.vendor} onChange={e => handleFilterChange('vendor', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-line">
                                    <input className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter', 'Filter')} value={filters.ponum} onChange={e => handleFilterChange('ponum', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-line">
                                    <select className={`w-full p-1.5 text-primary font-black outline-none border border-line-subtle bg-surface-card ${isRTL ? 'dir-rtl text-right' : ''}`} value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
                                        <option value="">{t('common.all', 'All')}</option>
                                        <option value="WAPPR">WAPPR</option>
                                        <option value="APPR">APPR</option>
                                        <option value="CLOSE">CLOSE</option>
                                        <option value="CAN">CAN</option>
                                    </select>
                                </td>
                                <td className="p-1 border-x border-line bg-surface-panel"></td>
                                <td className="p-1 border-x border-line bg-surface-panel"></td>
                                <td className="p-1 border-x border-line">
                                    <input className="w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card text-center" placeholder={t('common.filter', 'Filter')} value={filters.siteid} onChange={e => handleFilterChange('siteid', e.target.value)} />
                                </td>
                            </tr>
                        </thead>

                        <tbody className="divide-y font-bold">
                            {loading ? (
                                <tr><td colSpan="7" className="p-20 text-center text-ink-secondary font-black animate-pulse uppercase tracking-[0.2em]">{t('invoices.messages.syncing', 'Syncing Records...')}</td></tr>
                            ) : invoices.length > 0 ? (
                                invoices.map((inv, i) => (
                                    <tr key={i} className={`hover:bg-primary-soft cursor-pointer transition-colors group ${isRTL ? ' text-right' : 'text-left'}`} onClick={() => handleRowClick(inv.invoicenum)}>
                                        <td className={`p-3 border-x font-black text-primary group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>{inv.invoicenum}</td>
                                        <td className={`p-3 border-x text-ink font-bold italic ${isRTL ? 'border-l' : 'border-r'}`}>{inv.vendor || '---'}</td>
                                        <td className={`p-3 border-x text-ink-secondary font-bold ${isRTL ? 'border-l' : 'border-r'}`}>{inv.ponum || '---'}</td>
                                        <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                                            <StatusBadge status={inv.status} />
                                        </td>
                                        <td className={`p-3 border-x font-mono text-status-success font-black italic bg-surface-panel ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>
                                            {parseFloat(inv.grand_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                        </td>
                                        <td className={`p-3 border-x text-center text-ink-secondary font-bold ${isRTL ? 'border-l' : 'border-r'}`}>{inv.invoicedate?.split('T')[0]}</td>
                                        <td className="p-3 text-center text-ink-secondary font-black">{inv.siteid}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="7" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('invoices.messages.noData', 'No invoices match these filters')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Footer */}
            <div className={`bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black uppercase tracking-widest text-[10px] `}>
                <div className={`flex items-center gap-6 `}>
                    <div className={`flex items-center gap-2 `}>
                        <Box size={14} className="text-primary"/>
                        <span>{t('po.footer.showing', 'Showing')}: <span className="text-primary mx-1">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
                    </div>
                    <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r ' : 'border-l'}`}>
                        <Filter size={14} className="text-status-warning"/>
                        <span>{t('common.total', 'Total')}: <span className="text-status-warning mx-1">{total}</span></span>
                    </div>
                </div>

                <div className={`flex items-center gap-4 `}>
                    <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 `}>
                        <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <div className={`px-4 text-[11px] font-mono border-x border-white/10 mx-1 flex items-center gap-2 `}>
                            <span className="text-status-warning font-black text-sm">{page}</span>
                            <span className="text-ink-secondary text-[9px]">{t('common.of', 'of')} {totalPages}</span>
                        </div>
                        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SortableHeader = ({ label, sortKey, currentSort, onSort, align = "left", isRTL }) => {
    let justifyClass = 'justify-start';
    if (align === 'center') justifyClass = 'justify-center';
    if (align === 'right') justifyClass = isRTL ? 'justify-start' : 'justify-end';
    if (align === 'left' && isRTL) justifyClass = 'justify-end';

    return (
        <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-primary transition-colors ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => onSort(sortKey)}>
            <div className={`flex items-center gap-2 ${justifyClass} `}>
                {label}
                <ArrowUpDown size={12} className={currentSort.column === sortKey ? 'text-status-warning' : 'text-white/30'} />
            </div>
        </th>
    );
};

export default InvoiceList;
